import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/api/client';
import { DateRangePicker } from '@/components/DateRangePicker';
import { DailyCostChart, DailyCallsChart } from '@/components/charts';
import { RecentRequestsTable } from '@/components/RecentRequestsTable';
import { daysAgo, fmtNumber, fmtTokens, fmtUsd } from '@/lib/format';

export function MyUsage() {
  const api = useApi();
  const [range, setRange] = useState({ from: daysAgo(30), to: daysAgo(0) });

  const usage = useQuery({
    queryKey: ['my-usage', range.from, range.to],
    queryFn: () => api.myUsage(range.from, range.to)
  });
  const recent = useQuery({
    queryKey: ['my-recent-requests'],
    queryFn: () => api.myRecentRequests(50)
  });

  const daily = usage.data?.daily ?? [];
  const byModel = usage.data?.byModel ?? [];
  const totalCost = daily.reduce((s, d) => s + d.costUsd, 0);
  const totalCalls = daily.reduce((s, d) => s + d.calls, 0);

  return (
    <>
      <div className="page-header">
        <div>
          <h1>My usage</h1>
          <div className="sub">
            {range.from} to {range.to} &middot; {fmtNumber(totalCalls)} calls &middot; {fmtUsd(totalCost)}
          </div>
        </div>
      </div>

      <DateRangePicker from={range.from} to={range.to} onApply={(from, to) => setRange({ from, to })} />

      {usage.error && <div className="error-banner">Could not load usage: {(usage.error as Error).message}</div>}
      {usage.isLoading && <div className="placeholder">Loading usage...</div>}

      {usage.data && (
        <>
          <div className="grid-2">
            <div className="card chart-block">
              <div className="chart-title">Daily cost (USD)</div>
              {daily.length === 0
                ? <div className="placeholder">No usage in this period.</div>
                : <DailyCostChart data={daily} />}
            </div>
            <div className="card chart-block">
              <div className="chart-title">Daily calls</div>
              {daily.length === 0
                ? <div className="placeholder">No usage in this period.</div>
                : <DailyCallsChart data={daily} />}
            </div>
          </div>

          <div className="card">
            <div className="card-header"><h3>By model</h3></div>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Model</th>
                  <th className="num">Calls</th>
                  <th className="num">Tokens in</th>
                  <th className="num">Tokens out</th>
                  <th className="num">Cache read</th>
                  <th className="num">Cost</th>
                </tr>
              </thead>
              <tbody>
                {byModel.length === 0 ? (
                  <tr><td colSpan={6} className="placeholder">No usage in this period.</td></tr>
                ) : byModel.map(m => (
                  <tr key={m.group}>
                    <td><span className="row-title">{m.group}</span></td>
                    <td className="num">{fmtNumber(m.calls)}</td>
                    <td className="num">{fmtTokens(m.inputTokens)}</td>
                    <td className="num">{fmtTokens(m.outputTokens)}</td>
                    <td className="num">{fmtTokens(m.cacheReadInputTokens)}</td>
                    <td className="num">{fmtUsd(m.costUsd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <RecentRequestsTable
        rows={recent.data ?? []}
        isLoading={recent.isLoading}
        error={recent.error as Error | null}
        style={{ marginTop: 14 }}
      />
    </>
  );
}
